import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PublicHeader from '../components/PublicHeader';
import Seo from '../components/Seo';
import {
  AccountBalanceWallet,
  ArrowForward,
  CameraAlt,
  Description,
  FactCheck,
  Forum,
  Gavel,
  GpsFixed,
  Handshake,
  HomeWork,
  LockOutlined,
  Payments,
  ReceiptLong,
  Shield,
  Timer,
  VerifiedUser,
  Visibility,
  WorkspacePremium,
} from '@mui/icons-material';

/* ───────────────────────────────────────────────────────────────────────
   TRUST & SAFETY — public page stating Eqaya's core positioning:
   a TRUST platform, not just a construction marketplace. Shares the
   HomePage / ForContractorsPage design language (Figtree, Deep-Teal,
   eyebrow labels, tinted cards). Every claim on this page maps to a real,
   shipped platform feature — never promise what doesn't exist.
   ─────────────────────────────────────────────────────────────────────── */

const C = {
  green: '#185451',
  greenDeep: '#134e4a',
  ink: '#41464A',
  ink2: '#33373A',
  gray: '#5E6366',
  line: '#E7EBEA',
  card1: '#EAF3EC',
  card2: '#F1F4F3',
  card3: '#ECEFEE',
  tintBg: '#F5F7F6',
  ctaBg: '#3C4143',
  ctaAccent: '#6EE7D8',
};
const FONT = "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

const SectionLabel = ({ children, light = false, align = 'center' }) => (
  <Box sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: light ? C.ctaAccent : C.green, mb: '0.6rem', textAlign: align }}>
    {children}
  </Box>
);

const SectionH2 = ({ children, light = false, align = 'center' }) => (
  <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.8rem,3.5vw,2.6rem)', fontWeight: 800, letterSpacing: '-0.03em', color: light ? '#fff' : C.ink2, lineHeight: 1.15, textAlign: align, mb: '0.85rem', mt: 0 }}>
    {children}
  </Box>
);

const Bullet = ({ icon, title, body }) => (
  <Box sx={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
    <Box sx={{
      flexShrink: 0, width: 38, height: 38, borderRadius: '10px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(24,84,81,0.09)', color: C.green,
      '& svg': { fontSize: 20 },
    }}>
      {icon}
    </Box>
    <Box>
      <Box sx={{ fontWeight: 700, color: C.ink2, fontSize: '0.98rem', mb: '0.15rem' }}>{title}</Box>
      <Box sx={{ color: C.gray, fontSize: '0.92rem', lineHeight: 1.55 }}>{body}</Box>
    </Box>
  </Box>
);

const AudienceCard = ({ bg, eyebrow, title, bullets }) => (
  <Box sx={{ bgcolor: bg, borderRadius: '18px', p: 'clamp(1.4rem,2.5vw,2rem)', display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>
    <Box>
      <Box sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.green, mb: '0.35rem' }}>{eyebrow}</Box>
      <Box sx={{ fontFamily: FONT, fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em', color: C.ink2 }}>{title}</Box>
    </Box>
    {bullets.map((b) => <Bullet key={b.title} {...b} />)}
  </Box>
);

const TrustPage = () => {
  const navigate = useNavigate();
  const go = (path) => () => navigate(path);

  const clientBullets = [
    { icon: <LockOutlined />, title: 'Never pay cash upfront', body: 'Fund your Eqaya Wallet instead of handing money to anyone. It stays yours — not a cent moves until you say so.' },
    { icon: <Payments />, title: 'Release payments only when satisfied', body: 'Work is split into milestones. You release payment for each stage after you’ve seen it finished — never before.' },
    { icon: <Handshake />, title: 'Money linked to an accepted quotation', body: 'Your wallet connects to your contractor only once you accept their official quotation — so your money and your agreement always match.' },
    { icon: <VerifiedUser />, title: 'Contractors are actually verified', body: 'Documents and government IDs checked, and independent field verification with GPS-confirmed site visits to real past projects.' },
    { icon: <FactCheck />, title: 'Independent eyes on the ground', body: 'Hire an independent verifier for a property check or project verification — someone whose only job is telling you the truth.' },
    { icon: <Visibility />, title: 'Your privacy protected', body: 'Your street address is never shown publicly — contractors see your area, not your doorstep, until you choose to share it.' },
  ];

  const contractorBullets = [
    { icon: <AccountBalanceWallet />, title: 'The money is there before you start', body: 'When work is assigned to you, the agreed amount is already in the client’s funded wallet. No chasing, no empty promises.' },
    { icon: <Timer />, title: 'Finish a milestone, get paid in full', body: 'Complete a stage satisfactorily and the payment is released to you — and if the client doesn’t respond within 72 hours, it releases automatically.' },
    { icon: <Description />, title: 'No more deposit conversations', body: 'You never have to ask a stranger for a deposit again. Quote in small milestones and get paid regularly and fast.' },
    { icon: <WorkspacePremium />, title: 'A verified badge that wins work', body: 'Your verification, portfolio, reviews and trust score let clients choose you over an unknown name.' },
    { icon: <ReceiptLong />, title: 'Acceptance on record', body: 'An accepted quotation is a recorded commitment — the scope, the price and the client’s yes, all in writing.' },
    { icon: <Gavel />, title: 'Protected when things go wrong', body: 'Disputes are resolved on evidence — and the evidence is all there, because everything happened on Eqaya.' },
  ];

  const verifierBullets = [
    { icon: <Payments />, title: 'Fixed fees, no haggling', body: 'Accept a job knowing exactly what it pays. Your earnings are tracked in your ledger from the moment work is done.' },
    { icon: <GpsFixed />, title: 'GPS check-ins protect your name', body: 'Location-confirmed site check-ins prove you were really there — your reports carry weight because they can’t be faked.' },
    { icon: <Shield />, title: 'Your work stands on the record', body: 'Reports, photos and findings are stored with the project permanently. Your professionalism is visible to everyone who matters.' },
  ];

  const recordItems = [
    { icon: <Description />, label: 'Quotations & acceptances' },
    { icon: <Handshake />, label: 'Contracts & agreed deliverables' },
    { icon: <Forum />, label: 'Every message — even WhatsApp' },
    { icon: <CameraAlt />, label: 'Photos & videos of the work' },
    { icon: <ReceiptLong />, label: 'Invoices, receipts & payments' },
    { icon: <HomeWork />, label: 'Site visits & appointments' },
    { icon: <FactCheck />, label: 'Verification reports & site diaries' },
    { icon: <Gavel />, label: 'Milestones & change orders' },
  ];

  const primaryBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    px: '2.25rem', py: '0.95rem', borderRadius: '10px', minWidth: 200,
    fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: '#fff', bgcolor: C.green,
    cursor: 'pointer', border: 'none', textTransform: 'none', lineHeight: 1.2,
    transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(8,80,65,0.30)', opacity: 0.95 },
    '&:focus-visible': { outline: `3px solid ${C.ctaAccent}`, outlineOffset: 2 },
  };

  return (
    <Box sx={{ fontFamily: FONT, bgcolor: '#fff', color: C.ink, lineHeight: 1.5 }}>
      <Seo
        title="A Trust Platform for Diasporans Building Back Home"
        description="Eqaya is a trust platform for diasporans building, renovating and repairing property while you are away. Verified contractors do the work. Independent verifiers inspect it, with GPS proof and photos. You release payment from your wallet when satisfied."
        canonicalPath="/trust"
      />
      <PublicHeader />

      {/* ── HERO ── */}
      <Box component="section" sx={{
        position: 'relative', overflow: 'hidden', color: '#fff',
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3.5rem,7vw,6rem)',
        background: `radial-gradient(1100px 520px at 75% -10%, #1f6f6a 0%, ${C.green} 42%, ${C.greenDeep} 100%)`,
      }}>
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, opacity: 0.06, zIndex: 0,
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '46px 46px',
        }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
            fontSize: '0.78rem', fontWeight: 700, px: '0.95rem', py: '0.4rem', borderRadius: '100px', mb: '1.25rem',
          }}>
            <Shield sx={{ fontSize: 16 }} /> Trust &amp; Safety
          </Box>
          <Box component="h1" sx={{
            fontFamily: FONT, fontWeight: 800, fontSize: 'clamp(2.3rem,5vw,4rem)', lineHeight: 1.12,
            letterSpacing: '-0.04em', m: 0, mb: '1rem',
          }}>
            Build back home.<br />
            <Box component="span" sx={{ color: C.ctaAccent }}>While you&apos;re away.</Box>
          </Box>
          <Box component="p" sx={{
            fontSize: 'clamp(0.98rem,1.4vw,1.15rem)', color: 'rgba(255,255,255,0.9)',
            maxWidth: 680, mx: 'auto', mt: '0.5rem', mb: '2rem', fontWeight: 500, lineHeight: 1.65,
          }}>
            Eqaya is a <strong style={{ color: '#fff', fontWeight: 800 }}>trust platform</strong> for diasporans building,
            renovating and repairing property while you are away. <strong style={{ color: '#fff', fontWeight: 800 }}>Verified
            contractors</strong> do the work. <strong style={{ color: '#fff', fontWeight: 800 }}>Independent verifiers</strong> inspect
            it, with GPS proof and photos. You release payment from your wallet <strong style={{ color: '#fff', fontWeight: 800 }}>when
            satisfied</strong>.
          </Box>
          <Box sx={{
            display: 'grid', gap: '0.9rem', mb: '2rem', maxWidth: 900, mx: 'auto',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          }}>
            {[
              { icon: <VerifiedUser sx={{ fontSize: 22 }} />, step: '1', label: 'Verified contractors do the work' },
              { icon: <GpsFixed sx={{ fontSize: 22 }} />, step: '2', label: 'Independent verifiers inspect it — GPS proof & photos' },
              { icon: <AccountBalanceWallet sx={{ fontSize: 22 }} />, step: '3', label: 'You release payment when satisfied' },
            ].map((s) => (
              <Box key={s.step} sx={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', textAlign: 'left',
                bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: '14px', px: '1rem', py: '0.85rem',
              }}>
                <Box sx={{ color: C.ctaAccent, display: 'flex' }}>{s.icon}</Box>
                <Box sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.4 }}>{s.label}</Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box component="button" onClick={go('/register')} sx={{ ...primaryBtn, bgcolor: '#fff', color: C.greenDeep, '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.25)', bgcolor: '#fff' } }}>
              Get started free <ArrowForward sx={{ fontSize: 18 }} />
            </Box>
            <Box component="button" onClick={go('/contractors')} sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', px: '1.75rem', py: '0.95rem', borderRadius: '10px',
              fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: '#fff', cursor: 'pointer',
              bgcolor: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.6)', textTransform: 'none',
              transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
            }}>
              Browse verified contractors
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── THE EQAYA GUARANTEE ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,6vw,5rem)', bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <SectionLabel>The Eqaya Guarantee</SectionLabel>
          <SectionH2>Not a cent reaches a contractor until you&apos;ve seen the work and approved it</SectionH2>
          <Box component="p" sx={{ textAlign: 'center', color: C.gray, maxWidth: 720, mx: 'auto', mb: '2.25rem', fontSize: '1rem', lineHeight: 1.65 }}>
            Other platforms promise to refund you after something goes wrong. Eqaya is built so the
            money never leaves your control in the first place. Here is exactly what happens to every
            dollar you deposit.
          </Box>
          <Box sx={{ display: 'grid', gap: '0.9rem', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' } }}>
            {[
              { icon: <AccountBalanceWallet />, title: 'You deposit into your own wallet', body: 'The accepted quotation amount goes into an Eqaya wallet in your name. It is not paid to anyone. The contractor sees the project is funded and starts.' },
              { icon: <LockOutlined />, title: 'It stays locked while work happens', body: 'Each stage is funded separately. Nothing moves while the contractor is on site — no deposits, no advances, no "just send half now".' },
              { icon: <CameraAlt />, title: 'You see proof before you decide', body: 'The contractor submits the stage with photos. If you hired a verifier, their GPS-stamped inspection report reaches you first.' },
              { icon: <Handshake />, title: 'You approve — then we pay', body: 'Release happens on your approval. Disagree, and the stage is held for mediation until it is resolved. If you don’t respond within 72 hours of a submission, it releases automatically, so review promptly.' },
            ].map((s, i) => (
              <Box key={s.title} sx={{ bgcolor: i % 2 ? C.card2 : C.card1, borderRadius: '16px', p: '1.35rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(24,84,81,0.1)', color: C.green, '& svg': { fontSize: 22 } }}>{s.icon}</Box>
                <Box sx={{ fontSize: '0.74rem', fontWeight: 800, color: C.green, letterSpacing: '0.06em' }}>STEP {i + 1}</Box>
                <Box sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.05rem', color: C.ink2, letterSpacing: '-0.01em' }}>{s.title}</Box>
                <Box sx={{ color: C.gray, fontSize: '0.92rem', lineHeight: 1.55 }}>{s.body}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── THE PERMANENT RECORD ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,6vw,5rem)', bgcolor: C.tintBg }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <SectionLabel>Your construction record</SectionLabel>
          <SectionH2>One permanent home for everything about your build</SectionH2>
          <Box component="p" sx={{ textAlign: 'center', color: C.gray, maxWidth: 700, mx: 'auto', mb: '2.25rem', fontSize: '1rem', lineHeight: 1.65 }}>
            Paper receipts fade, chats get deleted, and &quot;but you said&quot; arguments have no referee.
            On Eqaya, everything about your project lives in one place, timestamped, for as long as you
            need it. When there&apos;s ever a disagreement, you have facts — not memories.
          </Box>
          <Box sx={{
            display: 'grid', gap: '0.9rem',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}>
            {recordItems.map((item) => (
              <Box key={item.label} sx={{
                display: 'flex', alignItems: 'center', gap: '0.8rem', bgcolor: '#fff',
                border: `1px solid ${C.line}`, borderRadius: '14px', px: '1.1rem', py: '0.95rem',
              }}>
                <Box sx={{ color: C.green, display: 'flex', '& svg': { fontSize: 22 } }}>{item.icon}</Box>
                <Box sx={{ fontWeight: 700, color: C.ink2, fontSize: '0.95rem' }}>{item.label}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── WHO IT PROTECTS ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,6vw,5rem)' }}>
        <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
          <SectionLabel>Built for every side of the build</SectionLabel>
          <SectionH2>What trust looks like on Eqaya</SectionH2>
          <Box sx={{
            display: 'grid', gap: '1.25rem', mt: '2rem',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, alignItems: 'start',
          }}>
            <AudienceCard bg={C.card1} eyebrow="For clients" title="Your money moves only when you say so" bullets={clientBullets} />
            <AudienceCard bg={C.card2} eyebrow="For contractors" title="Your payment is real before you lift a tool" bullets={contractorBullets} />
            <AudienceCard bg={C.card3} eyebrow="For verifiers" title="Your independence, on the record" bullets={verifierBullets} />
          </Box>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,6vw,5rem)', bgcolor: C.ctaBg, color: '#fff' }}>
        <Box sx={{ maxWidth: 820, mx: 'auto', textAlign: 'center' }}>
          <SectionLabel light>Build with confidence</SectionLabel>
          <SectionH2 light>Talk anywhere. Agree on Eqaya.</SectionH2>
          <Box component="p" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', lineHeight: 1.65, mb: '1.75rem' }}>
            Meet on site, chat on WhatsApp, call each other — then bring the agreement back as an
            official quotation and a funded wallet. That&apos;s the moment your project becomes protected.
          </Box>
          <Box component="button" onClick={go('/register')} sx={{ ...primaryBtn, bgcolor: C.ctaAccent, color: '#0c3835', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.35)', bgcolor: C.ctaAccent } }}>
            Create your free account <ArrowForward sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TrustPage;
